// The form of the desktop readout line (R1868, ui-map-interface.md §8 "The
// readout line").
//
// On a desktop the line is a strip centred over the map left of the dock
// column. At a large UI scale on a small screen the full line is wider than
// that room, and the strip ran off the left edge of the screen (R1858). The
// line then takes the narrow form of the telephone: no rates, no rules
// between the groups, and the most urgent timer stacked under the clock with
// its short label. Where even the narrow form is wider than the room, the
// line also starts at the left edge of the room, so its first cell stays on
// the screen; its right end then stands under the dock column.
//
// **The decision is measured, never guessed.** The room is in chrome px
// (R1556), so no media query can state it. This module reads the width of
// the full line and of the narrow line and writes one class on `#ui`:
// `readout-narrow`, with `readout-tight` beside it for the left start. The
// stylesheet reads the two classes. The telephone and the rail never carry
// them: their own sheet block gives the line its narrow form.
//
// **No loop.** The width of the full form is read while the full form
// stands, and read again under the narrow form only when the content key of
// the line changed: the markup with every digit as `0`, because the chrome
// face is monospaced and a new figure of the same length has the same width.
// A check runs at most once per animation frame, never inside the resize
// observer's own callback, so a change of the class that resizes the line
// never re-enters a callback of the same depth.

import { LAYOUT_PAD } from '../gen/presentation';
import { isSheet, SHEET_MQ } from './layout';
import { onUiScale } from './uiscale';

/** The three forms of the desktop line. */
export type ReadoutForm = 'full' | 'narrow' | 'tight';

/** The class of the narrow form on `#ui`. */
export const READOUT_NARROW = 'readout-narrow';
/** The class of the left start on `#ui`, beside `READOUT_NARROW`. */
export const READOUT_TIGHT = 'readout-tight';

/**
 * The form of the line: the full form where it fits its room, the narrow
 * form where that fits, and else the narrow form started at the left edge
 * of the room. Every width is in chrome px.
 */
export function readoutForm(full: number, narrow: number, room: number): ReadoutForm {
  if (full <= room) return 'full';
  if (narrow <= room) return 'narrow';
  return 'tight';
}

/**
 * The room of the line: the map left of the dock column, less one edge
 * margin at the screen edge and one at the dock. `dockLeft` is the left
 * edge of the dock in chrome px.
 */
export function readoutRoom(dockLeft: number, pad: number = LAYOUT_PAD): number {
  return Math.max(0, dockLeft - 2 * pad);
}

/** The content key of the line's markup: a change of it can change the
 *  width of the full form. A digit is `0`, because every digit of the
 *  monospaced face has one width. */
export function readoutKey(html: string): string {
  return html.replace(/\d/g, '0');
}

/** The width an element needs in chrome px: its border box, or its
 *  content where the content runs past the box. */
function needed(el: HTMLElement): number {
  const border = el.offsetWidth - el.clientWidth;
  return Math.max(el.offsetWidth, el.scrollWidth + border);
}

/**
 * The measured form of the line. `fit` decides and writes the classes;
 * `content` takes the markup of each rewrite of the line.
 */
export class ReadoutFit {
  private key = '';
  private fullKey: string | null = null;
  private full = 0;
  private pending = false;

  constructor(
    private readonly ui: HTMLElement,
    private readonly topbar: HTMLElement,
    private readonly dock: HTMLElement,
    private readonly schedule: (fn: () => void) => void = nextFrame,
    private readonly sheet: () => boolean = isSheet,
  ) {}

  /** The form in force, read off the classes. */
  form(): ReadoutForm {
    const c = this.ui.classList;
    return c.contains(READOUT_TIGHT) ? 'tight' : c.contains(READOUT_NARROW) ? 'narrow' : 'full';
  }

  /** Takes the markup of a rewrite of the line. A new content key asks
   *  for a check, because the full form can be wider or narrower now. */
  content(html: string): void {
    const key = readoutKey(html);
    if (key === this.key) return;
    this.key = key;
    this.request();
  }

  /** Asks for one check in the next frame. Many asks are one check. */
  request(): void {
    if (this.pending) return;
    this.pending = true;
    this.schedule(() => {
      this.pending = false;
      this.fit();
    });
  }

  /** Decides the form now and writes its classes. */
  fit(): void {
    if (this.sheet()) { this.write('full'); return; }
    // The full width: read live while the full form stands; under the
    // narrow form only after a change of the content, with the classes
    // off for the read and back on before the frame paints.
    const was = this.form();
    if (was !== 'full' && this.fullKey !== this.key) this.write('full');
    if (this.form() === 'full') {
      this.full = needed(this.topbar);
      this.fullKey = this.key;
    }
    const room = readoutRoom(this.dock.offsetLeft);
    if (this.full <= room) { this.write('full'); return; }
    this.write('narrow');
    this.write(readoutForm(this.full, needed(this.topbar), room));
  }

  /** Writes the classes of one form. A forced toggle of a class that
   *  already stands as asked changes nothing in the document. */
  private write(form: ReadoutForm): void {
    this.ui.classList.toggle(READOUT_NARROW, form !== 'full');
    this.ui.classList.toggle(READOUT_TIGHT, form === 'tight');
  }

  /** Forgets the full width, so the next check reads it again (a face
   *  that finished its load draws the same markup at a new width). */
  forget(): void {
    this.fullKey = null;
    this.request();
  }
}

function nextFrame(fn: () => void): void {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => fn());
  else setTimeout(fn, 0);
}

/**
 * Starts the measured form: a check after a change of the window, the
 * dock, the line itself, the UI scale, the form factor and the faces.
 */
export function installReadoutFit(ui: HTMLElement, topbar: HTMLElement, dock: HTMLElement): ReadoutFit {
  const fit = new ReadoutFit(ui, topbar, dock);
  if (typeof ResizeObserver === 'function') {
    const obs = new ResizeObserver(() => fit.request());
    obs.observe(ui);
    obs.observe(topbar);
    obs.observe(dock);
  }
  onUiScale(() => fit.request());
  SHEET_MQ.addEventListener('change', () => fit.request());
  if (typeof document !== 'undefined' && document.fonts) {
    document.fonts.addEventListener('loadingdone', () => fit.forget());
  }
  fit.request();
  return fit;
}
